import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import { ArticleCard, ArticleRow } from "@/components/feature/NewsCard";
import { usePageMeta, JsonLd } from "@/hooks/useSeo";
import { articles, columnMap } from "@/mocks/news";
import { columns } from "@/mocks/news";

export default function StoryPage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === id) ?? articles[0];
  const column = columnMap[article.column];

  const title = `${article.title} - 98爆料 ${column.name}`;
  const description = article.excerpt;
  usePageMeta({ title, description });

  const related = useMemo(
    () =>
      article.related
        .map((rid) => articles.find((a) => a.id === rid))
        .filter((a): a is (typeof articles)[number] => Boolean(a)),
    [article],
  );
  const columnLatest = articles
    .filter((a) => a.column === article.column && a.id !== article.id)
    .slice(0, 4);

  const jsonLd = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description,
      datePublished: `2026-${article.date.split(" ")[0]}`,
      author: { "@type": "Organization", name: "98爆料" },
      publisher: { "@type": "Organization", name: "98爆料", logo: "https://qlntroh.cn/logo.png" },
      image: article.image,
      mainEntityOfPage: `https://qlntroh.cn/story/${article.id}`,
      articleSection: column.name,
    }),
    [article, column, description],
  );

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={jsonLd} />
      <Header />

      <main>
        <div className="mx-auto max-w-7xl px-4 pt-5 md:px-6">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to={`/column/${column.slug}`} className="hover:text-primary-600">
              {column.name}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="line-clamp-1 font-medium text-foreground-800">正文</span>
          </nav>
        </div>

        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-4 py-6 md:px-6 lg:grid-cols-12">
          {/* 正文 */}
          <article className="lg:col-span-8" aria-label={`${article.title}正文`}>
            <div className="flex flex-wrap items-center gap-2">
              <span
                className="inline-flex items-center gap-1 rounded-full px-3 py-0.5 text-xs font-bold text-background-50"
                style={{ backgroundColor: column.accent }}
              >
                <i className={column.icon}></i>
                {column.name}
              </span>
              <span className="rounded-full bg-primary-100 px-3 py-0.5 text-xs font-bold text-primary-700">
                {article.tag}
              </span>
            </div>
            <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
              {article.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-4 border-y border-background-200 py-3 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 text-sm font-black text-primary-700">
                  <i className="ri-megaphone-fill text-xs"></i>
                </span>
                <span className="font-bold text-foreground-800">98编辑部</span>
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i>
                2026-{article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.views} 次围观
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-book-open-line"></i>
                阅读约 {article.readMin} 分钟
              </span>
            </div>

            <img
              src={article.image}
              alt={`${article.title} - ${column.name}`}
              title={`${article.title} ${column.name}`}
              className="mt-6 aspect-[16/9] w-full rounded-2xl object-cover object-top"
            />

            <p className="mt-6 border-l-4 border-primary-400 bg-primary-50/70 py-3 pl-4 font-heading text-base font-bold leading-relaxed text-primary-800 md:text-lg">
              {article.lead}
            </p>

            <div className="mt-6 space-y-5">
              {article.content.map((p, i) => (
                <p key={i} className="text-[15px] leading-8 text-foreground-700 md:text-base">
                  {p}
                </p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2 rounded-2xl bg-background-100 p-4">
              <span className="flex items-center gap-1 text-sm font-bold text-foreground-800">
                <i className="ri-price-tag-3-line text-primary-500"></i>
                本文话题
              </span>
              {[column.name, article.tag, ...hotListSlice()].map((t, i) => (
                <span
                  key={i}
                  className="cursor-pointer whitespace-nowrap rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-600 transition-colors hover:bg-primary-100 hover:text-primary-700"
                >
                  #{t}
                </span>
              ))}
            </div>
          </article>

          {/* 侧栏 */}
          <aside className="space-y-6 lg:col-span-4" aria-label="相关推荐">
            <div className="rounded-2xl border border-background-200 bg-background-100 p-5">
              <h2 className="flex items-center gap-2 font-heading text-lg font-black text-foreground-950">
                <i className="ri-fire-line text-primary-500"></i>
                同栏目更多瓜
              </h2>
              <div className="mt-3 flex flex-col divide-y divide-background-200">
                {columnLatest.map((a) => (
                  <ArticleRow key={a.id} article={a} column={columnMap[a.column]} />
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-background-200 bg-background-100 p-5">
              <h2 className="flex items-center gap-2 font-heading text-lg font-black text-foreground-950">
                <i className="ri-arrow-right-circle-line text-accent-600"></i>
                相关推荐
              </h2>
              <div className="mt-4 grid grid-cols-1 gap-4">
                {related.map((a) => (
                  <ArticleCard key={a.id} article={a} column={columnMap[a.column]} compact />
                ))}
              </div>
            </div>
          </aside>
        </div>

        {/* 其他栏目 */}
        <div className="border-t border-background-200 bg-background-100/60">
          <div className="mx-auto max-w-7xl px-4 py-10 md:px-6">
            <div className="flex flex-wrap items-center gap-2">
              <span className="flex items-center gap-1 whitespace-nowrap text-sm font-bold text-foreground-800">
                <i className="ri-compass-3-line text-primary-500"></i>
                逛逛更多栏目
              </span>
              {columns
                .filter((c) => c.slug !== column.slug)
                .map((c) => (
                  <Link
                    key={c.slug}
                    to={`/column/${c.slug}`}
                    className="flex items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-3.5 py-1.5 text-xs font-medium text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-700"
                  >
                    <i className={`${c.icon} text-accent-600`}></i>
                    {c.name}
                  </Link>
                ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

function hotListSlice() {
  return ["顶流动态", "路透现场", "综艺同框", "官宣定档"];
}